'use client'
// src/hooks/useNextRouter.ts

import { useEffect, useState, useRef } from "react";
import { useRouter } from "next/navigation";
import { dispatchRouteChangeEvent } from "@/utils/routeEvents";
import { AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime";

type RouterProxy = {
  push: AppRouterInstance["push"];
  back: AppRouterInstance["back"];
} & Omit<AppRouterInstance, "push" | "back">;

export default function useNextRouter(): AppRouterInstance {
  const router = useRouter();
  const [proxiedRouter, setProxiedRouter] = useState<AppRouterInstance | null>(
    null,
  );

  useEffect(() => {
    // Create a proxy for the router object
    const routerProxy: RouterProxy = new Proxy(router, {
      get: (target, prop: keyof AppRouterInstance) => {
        if (prop === "push" || prop === "back") {
          return (...args: any[]) => {
            // Dispatch the route change event before calling the actual method
            dispatchRouteChangeEvent("start");

            // Call the original method with the provided arguments
            return (target[prop] as Function)(...args);
          };
        }

        // For other properties/methods, return the original value
        return target[prop];
      },
    });

    // Set the proxy in the state
    setProxiedRouter(routerProxy);

    return () => {
      // Clean up the proxy reference (though in this case, it's not strictly necessary)
      setProxiedRouter(null);
    };
  }, [router]);
  // Return the proxied router if it's set, otherwise return null (or handle this case as needed)
  return proxiedRouter || router;
}
